export type RGB = { r: number; g: number; b: number };
export type RGBA = RGB & { a: number };
export type HSL = { h: number; s: number; l: number };
export type HSVA = { h: number; s: number; v: number; a: number };

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));
const clamp01 = (n: number) => clamp(n, 0, 1);
const rand = (min: number, max: number) => Math.random() * (max - min) + min;
const wrap360 = (h: number) => ((h % 360) + 360) % 360;

const normalizeHex = (hex: string): string => {
  let h = hex.trim().toLowerCase();
  if (!h.startsWith("#")) h = `#${h}`;

  if (h.length === 4) {
    // #rgb -> #rrggbb
    const r = h[1],
      g = h[2],
      b = h[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }

  if (h.length !== 7) {
    throw new Error(`Invalid hex color: "${hex}". Expected #RRGGBB or #RGB`);
  }

  return h;
};

export const hexToRgb = (hex: string): RGB => {
  const h = normalizeHex(hex);
  return {
    r: parseInt(h.slice(1, 3), 16),
    g: parseInt(h.slice(3, 5), 16),
    b: parseInt(h.slice(5, 7), 16),
  };
};

export const hexToRgba = (hex: string, a: number = 1): RGBA => {
  const { r, g, b } = hexToRgb(hex);
  return { r, g, b, a };
};

export const rgbToHex = ({ r, g, b }: RGB): string =>
  "#" + [r, g, b].map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, "0")).join("");

export const rgbToHsl = ({ r, g, b }: RGB): HSL => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));

    switch (max) {
      case r:
        h = ((g - b) / delta) % 6;
        break;
      case g:
        h = (b - r) / delta + 2;
        break;
      case b:
        h = (r - g) / delta + 4;
        break;
    }

    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s, l };
};

export const hslToRgb = ({ h, s, l }: HSL): RGB => {
  h = wrap360(h);
  s = clamp01(s);
  l = clamp01(l);

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0,
    g = 0,
    b = 0;

  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  return {
    r: (r + m) * 255,
    g: (g + m) * 255,
    b: (b + m) * 255,
  };
};

export const hslToHex = ({ h, s, l }: HSL): string => {
  const { r, g, b } = hslToRgb({ h, s, l });
  return rgbToHex({ r, g, b });
};

// Convert RGB to HSVA (Hue, Saturation, Value, Alpha)
// Note: @uiw/react-color expects s and v in 0-100 range
const rgbToHsva = ({ r, g, b }: RGB): HSVA => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) {
      h = ((g - b) / delta) % 6;
    } else if (max === g) {
      h = (b - r) / delta + 2;
    } else {
      h = (r - g) / delta + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  const s = max === 0 ? 0 : (delta / max) * 100;
  const v = max * 100;

  return { h, s, v, a: 1 };
};

// Convert HSVA to RGB
// Note: @uiw/react-color provides s and v in 0-100 range
const hsvaToRgb = ({ h, s, v, a: _a }: HSVA): RGB => {
  const sNorm = s / 100;
  const vNorm = v / 100;

  const c = vNorm * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = vNorm - c;

  let r = 0,
    g = 0,
    b = 0;

  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
};

// Convert hex to HSVA
export const hexToHsva = (hex: string): HSVA => {
  const rgb = hexToRgb(hex);
  return rgbToHsva(rgb);
};

// Convert HSVA to hex
export const hsvaToHex = (hsva: HSVA): string => {
  const rgb = hsvaToRgb(hsva);
  return rgbToHex(rgb);
};

/**
 * Generates a random hex color
 * @returns A random hex color in the format #RRGGBB
 */
export const generateRandomHexColor = (): string => {
  const color = Math.floor(Math.random() * 0xffffff);
  return `#${color.toString(16).padStart(6, "0")}`;
};

/**
 * Gets an opaque hex color
 * @param hex - The hex color to add opacity to
 * @param opacity - The opacity to add (0-1)
 * @returns A hex color with the added opacity
 */
export const getOpaqueHexColor = (hex: string, opacity: number = 0.5): string => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Generates a darker hex color by subtracting from each RGB channel (clamped)
 * @param hex - The hex color to darken
 * @param amount - Amount to subtract from each channel (0..255-ish)
 */
export const getDarkerHexColor = (hex: string, amount = 50): string => {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex({ r: r - amount, g: g - amount, b: b - amount });
};

/**
 * Generates a lighter hex color by adding to each RGB channel (clamped)
 * @param hex - The hex color to lighten
 * @param amount - Amount to add to each channel (0..255-ish)
 */
export const getLighterHexColor = (hex: string, amount = 50): string => {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex({ r: r + amount, g: g + amount, b: b + amount });
};

/**
 * Creates a brighter, more neon/vibrant version of a color.
 * Lightens the color while increasing saturation to create a neon effect.
 * This is designed to complement getDarkerPronouncedHexColor - when applied
 * to the same base color, they create a bright text/dark background pair.
 *
 * @param hex - The hex color to transform
 * @param lightnessTarget - Target lightness (0-1, default 0.82 = 82% lightness for bright neon colors)
 * @param saturationTarget - Target saturation (0-1, default 0.75 = 75% saturation for vibrant look)
 * @returns A brighter, more saturated hex color
 */
export const getBrighterPronouncedHexColor = (
  hex: string,
  lightnessTarget: number = 0.82,
  saturationTarget: number = 0.75,
): string => {
  const { r, g, b } = hexToRgb(hex);
  const { h, s } = rgbToHsl({ r, g, b });

  // Use target lightness for consistent bright colors
  const newLightness = clamp01(lightnessTarget);

  // Use target saturation for consistent vibrant colors
  // Preserve some of the original saturation character but boost it
  const newSaturation = clamp01(Math.max(saturationTarget, s * 1.2));

  return hslToHex({ h, s: newSaturation, l: newLightness });
};

/**
 * Creates a darker but more pronounced/vibrant version of a color.
 * Darkens the color while increasing saturation to maintain vibrancy.
 * This creates a rich, deep color that pairs perfectly with getBrighterPronouncedHexColor.
 * When both are applied to the same base color, they create complementary pairs.
 *
 * @param hex - The hex color to transform
 * @param lightnessTarget - Target lightness (0-1, default 0.18 = 18% lightness for dark rich colors)
 * @param saturationTarget - Target saturation (0-1, default 0.35 = 35% saturation for pronounced dark colors)
 * @returns A darker, more saturated hex color
 */
export const getDarkerPronouncedHexColor = (
  hex: string,
  lightnessTarget: number = 0.18,
  saturationTarget: number = 0.35,
): string => {
  const { r, g, b } = hexToRgb(hex);
  const { h, s } = rgbToHsl({ r, g, b });

  // Use target lightness for consistent dark colors
  const newLightness = clamp01(lightnessTarget);

  // Use target saturation for consistent rich dark colors
  // Preserve some of the original saturation character but ensure it's pronounced
  const newSaturation = clamp01(Math.max(saturationTarget, s * 1.1));

  return hslToHex({ h, s: newSaturation, l: newLightness });
};

/**
 * Random pastel color (soft, light, UI-friendly)
 */
export const generateRandomPastelHexColor = (): string => {
  return hslToHex({ h: rand(0, 360), s: rand(0.25, 0.55), l: rand(0.75, 0.9) });
};

/**
 * Random vibrant color (saturated, bold, UI-friendly)
 * Generates colors similar to: #5D9CEC, #4FC1E9, #48CFAD, #A0D468, #FFCE54, #FC6E51, #ED5565, #AC92EC, #EC87C0
 */
export const generateRandomVibrantHexColor = (): string => {
  // Balanced hue ranges (greens included but not dominant)
  const hueRanges: [number, number][] = [
    [0, 60], // reds, oranges
    [60, 90], // yellows, yellow-greens
    [90, 150], // greens
    [150, 210], // cyans
    [210, 270], // blues
    [270, 330], // purples, magentas
    [330, 360], // pinks, reds
  ];
  const range = hueRanges[Math.floor(Math.random() * hueRanges.length)];
  const h = rand(range[0], range[1]);
  return hslToHex({ h, s: rand(0.6, 0.95), l: rand(0.5, 0.75) });
};
